import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const FEATURE_MAP = {
  '/dashboard': 'dashboard',
  '/upload': 'upload',
  '/transactions': 'review',
  '/journal': 'journal',
  '/accounts': 'accounts',
  '/invoices': 'invoices',
  '/customers': 'customers',
  '/reports': 'reports',
  '/settings': 'settings',
  '/platform-admin': 'admin',
};

export function useFeatureTracker() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const entryTime = useRef(null);
  const currentFeature = useRef(null);

  useEffect(() => {
    if (!user) return;

    // Find matching feature
    const feature = Object.entries(FEATURE_MAP).find(([path]) => pathname.startsWith(path));
    const featureName = feature ? feature[1] : null;

    // Track time spent on previous feature
    if (currentFeature.current && entryTime.current) {
      const duration = Date.now() - entryTime.current;
      if (duration > 2000) { // Only track if > 2 seconds
        analyticsAPI.track(currentFeature.current, 'view', duration).catch(() => {});
      }
    }

    // Start tracking new feature
    currentFeature.current = featureName;
    entryTime.current = featureName ? Date.now() : null;

    // Track page view
    if (featureName) {
      analyticsAPI.track(featureName, 'visit', null, { path: pathname }).catch(() => {});
    }
  }, [pathname, user]);

  // Track on unmount (tab close / navigation away)
  useEffect(() => {
    return () => {
      if (currentFeature.current && entryTime.current) {
        const duration = Date.now() - entryTime.current;
        if (duration > 2000) {
          analyticsAPI.track(currentFeature.current, 'view', duration).catch(() => {});
        }
      }
    };
  }, []);
}

// Helper to track specific actions (upload complete, report generated, etc.)
export function trackAction(feature, action, durationMs, metadata) {
  analyticsAPI.track(feature, action, durationMs, metadata).catch(() => {});
}
